import React, { useState } from "react";
import { TrendingUp, TrendingDown, Minus, Download, Calendar } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar, Cell } from "recharts";
import { Button } from "../../components/ui/button";

export const RANGES = [
  { key: "today", label: "Oggi" }, { key: "7d", label: "7 giorni" }, { key: "30d", label: "30 giorni" }, { key: "90d", label: "90 giorni" }, { key: "custom", label: "Personalizzato" },
];
const PALETTE = ["#123C46", "#2f7f78", "#8fd7c4", "#c9a227", "#b4541a", "#6b7f83", "#9bb0b4"];

export const fmtDate = (s, gran) => {
  if (!s) return "";
  if (gran === "month") { const [y, mo] = s.split("-"); return new Date(+y, +mo - 1, 1).toLocaleDateString("it-IT", { month: "short", year: "2-digit" }); }
  const d = new Date(s + "T00:00:00");
  return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short" });
};

export function RangePicker({ value, custom, onChange, csvUrl }) {
  const [from, setFrom] = useState(custom?.date_from || "");
  const [to, setTo] = useState(custom?.date_to || "");
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="range-picker">
      {RANGES.map((r) => (
        <button key={r.key} onClick={() => onChange(r.key, r.key === "custom" && from ? { date_from: from, date_to: to || from } : null)} data-testid={`range-${r.key}`}
          className={`h-9 px-3.5 rounded-full text-sm font-semibold border transition-colors ${value === r.key ? "bg-primary text-white border-primary" : "bg-white border-border text-foreground/70 hover:border-primary/40"}`}>
          {r.label}
        </button>
      ))}
      {value === "custom" && (
        <div className="flex items-center gap-1.5 text-sm" data-testid="custom-range">
          <Calendar className="h-4 w-4 text-muted-foreground" />
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 rounded-lg border border-border px-2 bg-white" data-testid="range-from" />
          <span className="text-muted-foreground">→</span>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 rounded-lg border border-border px-2 bg-white" data-testid="range-to" />
          <Button size="sm" variant="outline" disabled={!from} onClick={() => onChange("custom", { date_from: from, date_to: to || from })} data-testid="range-apply">Applica</Button>
        </div>
      )}
      {csvUrl && <a href={csvUrl} className="ml-auto inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-border bg-white text-sm font-semibold hover:bg-muted" data-testid="export-csv"><Download className="h-4 w-4" /> CSV</a>}
    </div>
  );
}

export function Delta({ pct }) {
  if (pct === null || pct === undefined) return <span className="text-xs text-muted-foreground inline-flex items-center gap-1"><Minus className="h-3 w-3" /> nessun dato precedente</span>;
  const up = pct > 0, flat = pct === 0;
  return (
    <span className={`text-xs font-semibold inline-flex items-center gap-1 ${flat ? "text-muted-foreground" : up ? "text-emerald-600" : "text-rose-600"}`} data-testid="delta">
      {flat ? <Minus className="h-3 w-3" /> : up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
      {up ? "+" : ""}{pct}% vs periodo precedente
    </span>
  );
}

export function KPI({ label, value, sub, testid }) {
  return (
    <div className="rounded-2xl border border-border bg-white p-4 card-shadow" data-testid={testid}>
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      <p className="text-2xl md:text-3xl font-display font-extrabold mt-1 tabular-nums">{value}</p>
      {sub && <div className="mt-1">{sub}</div>}
    </div>
  );
}

export function TrendChart({ series, gran, testid = "trend-chart", height = 240 }) {
  const data = (series || []).map((p) => ({ ...p, label: fmtDate(p.date, gran) }));
  const empty = data.every((p) => !p.scans);
  return (
    <div className="rounded-2xl border border-border bg-white p-4 card-shadow" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <p className="font-semibold">Scansioni nel tempo</p>
        <span className="text-xs text-muted-foreground">{gran === "day" ? "per giorno" : gran === "week" ? "per settimana" : "per mese"}</span>
      </div>
      {empty && <p className="text-xs text-muted-foreground mb-1">Nessuna scansione nel periodo selezionato.</p>}
      <div style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 8, right: 8, left: -22, bottom: 0 }}>
            <defs><linearGradient id="g1" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#123C46" stopOpacity={0.35} /><stop offset="100%" stopColor="#123C46" stopOpacity={0.02} /></linearGradient></defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e6ebea" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#6b7f83" }} tickLine={false} axisLine={false} interval="preserveStartEnd" minTickGap={24} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#6b7f83" }} tickLine={false} axisLine={false} />
            <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e6ebea", fontSize: 12 }} formatter={(v) => [v, "Scansioni"]} />
            <Area type="monotone" dataKey="scans" stroke="#123C46" strokeWidth={2.2} fill="url(#g1)" dot={data.length <= 31} activeDot={{ r: 4 }} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export function Breakdown({ title, items, testid, emptyText = "Nessun dato nel periodo." }) {
  const total = (items || []).reduce((s, x) => s + x.value, 0);
  return (
    <div className="rounded-2xl border border-border bg-white p-4 card-shadow" data-testid={testid}>
      <p className="font-semibold mb-3">{title}</p>
      {!items || items.length === 0 || total === 0 ? <p className="text-sm text-muted-foreground">{emptyText}</p> : (
        <div className="space-y-2.5">
          {items.slice(0, 7).map((x, i) => {
            const pct = Math.round(x.value / total * 100);
            return (
              <div key={x.label}>
                <div className="flex justify-between text-sm"><span className="truncate pr-3">{x.label}</span><span className="tabular-nums text-muted-foreground shrink-0">{x.value} · {pct}%</span></div>
                <div className="h-1.5 rounded-full bg-muted mt-1 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${pct}%`, background: PALETTE[i % PALETTE.length] }} /></div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function BarsChart({ title, items, testid, height = 180 }) {
  const empty = (items || []).every((x) => !x.value);
  return (
    <div className="rounded-2xl border border-border bg-white p-4 card-shadow" data-testid={testid}>
      <p className="font-semibold mb-2">{title}</p>
      {empty ? <p className="text-sm text-muted-foreground">Nessun dato nel periodo.</p> : (
        <div style={{ height }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={items} margin={{ top: 4, right: 4, left: -26, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e6ebea" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#6b7f83" }} tickLine={false} axisLine={false} interval={items.length > 12 ? 2 : 0} />
              <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#6b7f83" }} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e6ebea", fontSize: 12 }} formatter={(v) => [v, "Scansioni"]} />
              <Bar dataKey="value" radius={[6, 6, 0, 0]}>{items.map((_, i) => <Cell key={i} fill="#123C46" fillOpacity={0.85} />)}</Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

export function PrivacyNote() {
  return (
    <p className="text-[11px] text-muted-foreground leading-relaxed" data-testid="privacy-note">
      <b>Come misuriamo.</b> Registriamo solo le scansioni che passano dal redirect Qora (QR dinamici): data/ora, tipo dispositivo, sistema operativo e browser (dallo user-agent), eventuale sito di provenienza.
      L'indirizzo IP non viene mai salvato. <b>«Visitatori stimati»</b> è una stima: hash giornaliero non reversibile di dispositivo+rete, non equivale a persone reali. Nessuna posizione GPS; l'area geografica compare solo se la rete la fornisce in forma approssimativa (paese).
    </p>
  );
}
